/**
 * Turnstile Component
 * Renders a Cloudflare Turnstile CAPTCHA widget
 */

import { useEffect, useRef, useCallback } from 'react';
import { useThemeStore } from '../../stores/theme.store';
import { cn } from '../../lib/utils';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptLoaded = false;
let scriptLoading = false;
const loadCallbacks: (() => void)[] = [];

function loadTurnstileScript(): Promise<void> {
  if (scriptLoaded) return Promise.resolve();

  return new Promise((resolve) => {
    if (scriptLoading) {
      loadCallbacks.push(resolve);
      return;
    }

    scriptLoading = true;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      resolve();
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };
    script.onerror = () => {
      scriptLoading = false;
      resolve(); // Resolve anyway — graceful degradation
    };
    document.head.appendChild(script);
  });
}

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  size?: 'normal' | 'compact' | 'flexible';
  action?: string;
  className?: string;
  resetRef?: React.MutableRefObject<(() => void) | null>;
}

export const Turnstile = ({
  onVerify,
  onExpire,
  onError,
  size = 'flexible',
  action,
  className,
  resetRef,
}: TurnstileProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const isDark = useThemeStore((s) => s.isDark);
  const theme = isDark ? 'dark' : 'light';

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile || !siteKey) return;

    // Remove existing widget if present
    if (widgetIdRef.current !== null) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch {
        // Widget may already be removed
      }
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      callback: onVerify,
      'expired-callback': onExpire,
      'error-callback': onError,
      theme,
      size,
      action,
    });

    // Expose reset function to parent
    if (resetRef) {
      resetRef.current = () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      };
    }
  }, [siteKey, onVerify, onExpire, onError, theme, size, action, resetRef]);

  useEffect(() => {
    if (!siteKey) return;

    loadTurnstileScript().then(() => {
      renderWidget();
    });

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget may already be removed
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, renderWidget]);

  // Render nothing if site key not configured (graceful degradation)
  if (!siteKey) return null;

  return (
    <div className={cn('relative', className)}>
      <div ref={containerRef} />
      <div className="hidden dark:block absolute inset-0 bg-[var(--bg-app)] mix-blend-hue pointer-events-none rounded" />
      <div className="hidden dark:block absolute inset-0 bg-[var(--bg-app)] mix-blend-multiply opacity-40 pointer-events-none rounded" />
      <div className="hidden dark:block absolute inset-0 border-2 border-[var(--bg-app)] pointer-events-none" />
    </div>
  );
};
